import {
  ComponentIds,
  Entity,
  ProductUtils,
  SelectedComponent,
  WorldTransformComponent
} from '@inter-ikea-core/core';
import { Product, ProductType, SelectionOverrideComponent } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { useAppDispatch } from 'ducks/hooks';
import { getAgeGroup } from 'ducks/layout';
import {
  getConfigSorting,
  getFilterState,
  getMarketProducts,
  getSelectedGroupId
} from 'ducks/products';
import { setShowSelectionOverrideIndicators } from 'ducks/selectionOverride';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isMobile } from 'utils/platform';
import InteriorSuggestionsDesktop from '../../shared/widgets/interiorSuggestions/InteriorSuggestionsDesktop';
import InteriorSuggestionsMobile from '../../shared/widgets/interiorSuggestions/InteriorSuggestionsMobile';
import addSmastadInteriorSuggestion from '../behaviors/addSmastadInteriorSuggestion';
import { framePlacementOkForAssembly } from '../rules/interiorPlacementRules';
import SelectInSceneImage from './images/InteriorSuggestionsSelectInScene.png';
import { store } from 'ducks';
import { useBehaviorTracker } from 'contexts/behaviors';
import { useWidget } from 'contexts/widget';
import { BatchType, ProductBatch } from 'utils/batch';
import { noneOptionForInteriorSuggestions } from 'rangeConfig/jonaxel/widgets/noneOptionForInteriorSuggestions';

const frameTypes = [
  ProductType.SMASTAD_FRAME,
  ProductType.SMASTAD_FRAME_NA,
  ProductType.SMASTAD_PULL_OUT_FRAME_108_ASM,
  ProductType.SMASTAD_PULL_OUT_FRAME_196_ASM
];
const interiorSuggestionTypes = [ProductType.SMASTAD_INTERIOR_SUGGESTION];

const drawerInInteriorSuggestions = (product: Product) => {
  const typesInSuggestions = product.parts.map(part => {
    return part.product?.type;
  });
  return typesInSuggestions.includes(ProductType.SMASTAD_DRAWER_INSERT);
};

export default function InteriorSuggestions() {
  const dispatch = useAppDispatch();
  const rootState = store.getState();
  const products = useSelector(getMarketProducts);
  const coreApi = useCore();
  const entityManager = coreApi.getServices().entityManager;
  const filterState = useSelector(getFilterState);
  const selectedGroupId = useSelector(getSelectedGroupId);
  const { configId } = useWidget();
  const sorting = useSelector(getConfigSorting(configId));
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [interiorSuggestions, setInteriorSuggestions] = useState<Product[]>([]);
  const currentGlobalAgeGroup = useSelector(getAgeGroup);
  const behaviorTracker = useBehaviorTracker();

  const interiorSuggestionProducts = useMemo(() => {
    return products.filter(p => interiorSuggestionTypes.includes(p.type));
  }, [products]);

  const frameProducts = useMemo(() => {
    return products.filter(p => frameTypes.includes(p.type));
  }, [products]);

  const optionsMap: Record<string, ProductType[]> = filterState(selectedGroupId, 'type');

  const selectedOptionsTypes = Object.values(optionsMap).filter(o => o.length > 0);

  const selectedEntityHeight =
    selectedEntity?.getComponent<WorldTransformComponent>(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)
      ?.transform.position.Y || 0;

  useEffect(() => {
    if (selectedEntity) {
      const frameProduct = frameProducts.find(
        p => p.coreProductId === ProductUtils.productRef(selectedEntity)
      );

      let suggestionsForSelectedFrame = interiorSuggestionProducts.filter(p => {
        return frameProduct
          ? p.communicatedDimensions.width === frameProduct.communicatedDimensions.width &&
              p.communicatedDimensions.depth === frameProduct.communicatedDimensions.depth &&
              p.communicatedDimensions.height === frameProduct.communicatedDimensions.height &&
              p.design === (filterState(selectedGroupId, 'design').design || frameProduct.design) &&
              framePlacementOkForAssembly(selectedEntity, p, coreApi) &&
              p.meta?.ageGroup &&
              p.meta?.ageGroup === currentGlobalAgeGroup
          : false;
      });

      suggestionsForSelectedFrame = suggestionsForSelectedFrame.filter(product => {
        const partTypes = product.parts.flatMap(part => part.product?.type || []);
        const allOptionsSatisfied = selectedOptionsTypes.every(selectedTypes =>
          selectedTypes.find(type => partTypes.includes(type))
        );
        return allOptionsSatisfied;
      });

      setInteriorSuggestions(suggestionsForSelectedFrame);

      const selectedFrameContainsDrawers = coreApi.entities.getChildrenWithProductType(
        selectedEntity,
        ProductType.SMASTAD_DRAWER_ASM
      )[0];

      if (selectedFrameContainsDrawers) {
        const sortedSuggestions = suggestionsForSelectedFrame.sort((a: any) => {
          if (drawerInInteriorSuggestions(a)) {
            return -1;
          }

          return 0;
        });

        setInteriorSuggestions(sortedSuggestions);
      }
    }
    if (sorting?.compare)
      setInteriorSuggestions(interiorSuggestions =>
        interiorSuggestions.sort(
          ProductBatch.comparison(
            sorting.compare as NonNullable<typeof sorting['compare']>,
            BatchType.Product
          )
        )
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity, currentGlobalAgeGroup, selectedEntityHeight, sorting]);

  const addSelectionOverrideComponentToFrames = useCallback(() => {
    const framesInScene = coreApi.entities.getByProductTypes(frameTypes);

    framesInScene.forEach(frame => frame.addComponent(new SelectionOverrideComponent()));
    if (framesInScene.length === 1) {
      // If only one frame in scene, select it
      framesInScene[0].addComponent(new SelectedComponent());
      setSelectedEntity(framesInScene[0]);
    }
  }, [coreApi]);

  const removeSelectionOverrideComponentFromFrames = useCallback(() => {
    const framesInScene = coreApi.entities
      .getByComponent(ComponentIds.FRAME_COMPONENT_ID)
      .filter(f => !f.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID));
    framesInScene.forEach(frame => frame.removeComponent(SelectionOverrideComponent.id));
  }, [coreApi]);

  useEffect(() => {
    addSelectionOverrideComponentToFrames();
    dispatch(setShowSelectionOverrideIndicators(true));
    return () => {
      removeSelectionOverrideComponentFromFrames();
      dispatch(setShowSelectionOverrideIndicators(false));
      coreApi.entities.unselect();
    };
  }, [
    addSelectionOverrideComponentToFrames,
    coreApi,
    dispatch,
    removeSelectionOverrideComponentFromFrames
  ]);

  useEffect(() => {
    const onSelectedEntity = (entity: Entity) => {
      if (entity.hasComponent(SelectionOverrideComponent.id)) {
        setSelectedEntity(entity);
      }
    };
    const onUnSelectedEntity = (entity: Entity) => {
      if (entity.hasComponent(SelectionOverrideComponent.id)) {
        setSelectedEntity(null);
      }
    };

    entityManager.subscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, onSelectedEntity);
    entityManager.subscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, onUnSelectedEntity);
    return () => {
      entityManager.unsubscribeAdd(ComponentIds.SELECTED_COMPONENT_ID, onSelectedEntity);
      entityManager.unsubscribeRemove(ComponentIds.SELECTED_COMPONENT_ID, onUnSelectedEntity);
    };
  }, [entityManager]);

  const handleClick = (product: Product) => {
    behaviorTracker.withMeta({ targets: [product] }).custom(() => {
      const props = {
        coreProductId: product.coreProductId,
        menu: 'main_menu'
      };

      addSmastadInteriorSuggestion({ coreApi, dispatch, rootState, props });
    });
  };
  return isMobile() ? (
    <InteriorSuggestionsMobile
      selectedEntity={selectedEntity}
      selectedGroupId={selectedGroupId}
      suggestionsForSelectedFrame={interiorSuggestions}
      handleClick={handleClick}
      message="storageone.paxSelectFrameInSceneChatBubbleMessage"
      image={SelectInSceneImage}
      noneOptionForInteriorSuggestions={noneOptionForInteriorSuggestions}
    />
  ) : (
    <InteriorSuggestionsDesktop
      selectedEntity={selectedEntity}
      selectedGroupId={selectedGroupId}
      suggestionsForSelectedFrame={interiorSuggestions}
      handleClick={handleClick}
      message="storageone.paxSelectFrameInSceneChatBubbleMessage"
      image={SelectInSceneImage}
      noneOptionForInteriorSuggestions={noneOptionForInteriorSuggestions}
    />
  );
}
